<template>
  <div class="flex flex-col min-h-full w-full bg-white select-none">
    <!-- 頂部品牌標題區：提供頂部安全區域安全邊距預設值 -->
    <div 
      class="border-b border-gray-100 bg-white shrink-0"
      :style="{
        paddingTop: 'calc(1.5rem + var(--safe-area-top, 0px))',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        paddingBottom: '1.5rem'
      }"
    >
      <div class="text-2xl font-bold bg-linear-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent tracking-tight">
        {{ t('admin.dashboard') }}
      </div>
    </div>

    <!-- 彈性流動導覽列表：支援局部獨立滾動，不干涉全域滾動軸 -->
    <nav class="flex-1 w-full px-4 py-4 overflow-y-auto overscroll-contain">
      <ul class="space-y-2" :style="{ paddingBottom: 'calc(2rem + var(--safe-area-bottom, 0px))' }">
        <li v-for="item in visibleMenuItems" :key="item.path">
          <router-link
            :to="item.path"
            @click="handleClick"
            class="nav-menu-link group"
            active-class="nav-menu-link-active"
          >
            <span class="nav-menu-indicator"></span>
            <span class="text-lg font-semibold tracking-wide transition-colors">{{ t(item.name) }}</span>
            <span class="ml-auto text-blue-400 text-xl transition-transform group-active:translate-x-1">›</span>
          </router-link>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ROUTES } from '@myorg/type-lib'
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const menuItems = [
  { name: ROUTES.USER.SHOW, path: ROUTES.USER.PATH, roles: ['admin', 'merchant'] },
  { name: ROUTES.MENU.SHOW, path: ROUTES.MENU.PATH, roles: ['admin', 'merchant'] },
  { name: ROUTES.UPLOAD_R2.SHOW, path: ROUTES.UPLOAD_R2.PATH, roles: ['admin', 'merchant'] },
  { name: ROUTES.BOOKING.CONFIG.SHOW, path: ROUTES.BOOKING.CONFIG.PATH, roles: ['admin', 'merchant'] },
  { name: ROUTES.BOOKING.RESERVATION.SHOW, path: ROUTES.BOOKING.RESERVATION.PATH, roles: ['admin', 'merchant', 'employee'] },
  { name: ROUTES.REPORT.SHOW, path: ROUTES.REPORT.PATH, roles: ['admin', 'merchant'] },
  { name: ROUTES.ECPAY.SHOW, path: ROUTES.ECPAY.PATH, roles: ['admin', 'merchant', 'employee'] },
  { name: ROUTES.TABLE_POST.SHOW, path: ROUTES.TABLE_POST.PATH, roles: ['admin', 'merchant', 'employee'] },
  { name: ROUTES.ALL_ORDER.SHOW, path: ROUTES.ALL_ORDER.PATH, roles: ['admin', 'merchant', 'employee'] },
]

const getStoredRole = () => {
  if (typeof window === 'undefined') return 'guest'
  const rolesRaw = localStorage.getItem('userRoles')
  if (!rolesRaw) return 'guest'
  try {
    const roles = JSON.parse(rolesRaw) as string[]
    if (roles.includes('ROLE_ADMIN')) return 'admin'
    if (roles.includes('ROLE_MERCHANT')) return 'merchant'
    if (roles.includes('ROLE_MERCHANT_EMP')) return 'employee'
    return 'guest'
  } catch {
    return 'guest'
  }
}

const currentUserRole = ref<string | null>(getStoredRole())

onMounted(() => {
  window.addEventListener('storage', () => {
    currentUserRole.value = getStoredRole()
  })
})

const visibleMenuItems = computed(() => {
  const role = currentUserRole.value
  return menuItems.filter(item => {
    if (!item.roles || item.roles.length === 0) return true
    return role && item.roles.includes(role)
  })
})

const emit = defineEmits(['link-click'])
const handleClick = () => emit('link-click')
</script>

<style scoped>
/* 封裝特定類名樣式，絕不污染基礎語意標籤 */
.nav-menu-link {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  min-height: 3.25rem;
  border-radius: 0.75rem;
  color: #374151;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
  touch-manipulation: manipulation;
}

/* 移除純 hover 依賴，加入明確的觸控 :active 狀態回饋 */
@media (hover: hover) {
  .nav-menu-link:hover {
    background-color: #f8fafc;
    color: #1d4ed8;
  }
}

.nav-menu-link:active {
  background-color: #f1f5f9;
  transform: scale(0.98);
}

.nav-menu-link-active {
  background-color: #eff6ff !important;
  color: #1d4ed8 !important;
}

.nav-menu-indicator {
  width: 4px;
  height: 1.25rem;
  background-color: transparent;
  margin-right: 0.75rem;
  border-radius: 2px;
  flex-shrink: 0;
}

.nav-menu-link-active .nav-menu-indicator {
  background-color: #1d4ed8;
}
</style>